import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import * as Icons from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import ScrollReveal from '../components/ScrollReveal';
import SEO from '../components/SEO';
import api from '../utils/api';

const Services = () => {
  const { t, language } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await api.get('/services?active_only=true');
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const getIcon = (iconName) => {
    const IconComponent = Icons[iconName];
    return IconComponent ? <IconComponent className="w-6 h-6" /> : <Icons.Layers className="w-6 h-6" />;
  };

  return (
    <div className="min-h-screen pt-24 pb-20 bg-bms-light" data-testid="services-page">
      <SEO 
        title="Hizmetler"
        description="BrightMind Software hizmetleri - Kurumsal yazılım, yapay zeka entegrasyonu, web ve mobil uygulama geliştirme."
        keywords="yazılım hizmetleri, kurumsal çözümler, yapay zeka, web geliştirme, mobil uygulama"
      />
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <ScrollReveal>
          <div className="text-center mb-16">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-bms-text-light hover:text-bms-primary mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.pages.backToHome}
            </Link>
            <h1 className="text-3xl md:text-5xl font-bold text-bms-text mb-4">
              {t.services.title}
            </h1>
            <p className="text-lg text-bms-text-light max-w-2xl mx-auto">
              {t.services.subtitle}
            </p>
          </div>
        </ScrollReveal>

        {/* Services Grid */}
        {loading ? (
          <div className="text-center text-bms-text-light">{t.pages.loading}</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const title = language === 'tr' ? service.title_tr : service.title_en;
              const description = language === 'tr' ? service.short_desc_tr : service.short_desc_en;
              
              return (
                <ScrollReveal key={service.id || index} delay={index * 0.1}>
                  <motion.div
                    whileHover={{ y: -4 }}
                    className="bg-white rounded-xl p-6 border border-gray-100 shadow-corporate h-full flex flex-col"
                  >
                    <div className="icon-container mb-4">
                      {getIcon(service.icon)}
                    </div>
                    <h3 className="text-lg font-semibold text-bms-text mb-2">{title}</h3>
                    <p className="text-bms-text-light text-sm mb-4 flex-1">{description}</p>
                    <Link 
                      to={`/hizmetler/${service.slug}`}
                      className="inline-flex items-center gap-1 text-bms-primary text-sm font-medium hover:gap-2 transition-all"
                    >
                      {t.services.detailBtn}
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </motion.div>
                </ScrollReveal>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Services;
